import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import SubmitBtn from 'src/component/ActionBtn/SubmitBtn'
import CostTag from 'src/component/CostTag'
import MedicinesSubmitionPopup from 'src/component/MedicinesPopup'
import PopupContainer from 'src/component/popup/PopupContainer'
import { useMutation } from 'src/hooks/useMutation'
import { GetMedicinesForManualReceive, SubmitManualRequest } from 'src/requests/ManualRequests'
import { SubmitChangeRequestMedicinesRequest } from 'src/requests/Shared'
import { errorToaster, getHospitalConfiguralion } from 'src/utils'
import SummaryRequestMedicines from '../../../component/MedicinesPopup/SummaryRequestMedicines'
import { PatientData } from 'src/types/patientData'
import { usePrintImages } from 'src/hooks/usePrintImages'
import { ALLOWED_MONTHS_TO_COVER } from 'src/common/constants'
import BatchDispensePopup from 'src/component/popup/BatchDispensePopup/BatchDispensePopup'
import PrintRequest from 'src/component/PrintedPDF/PrintRequestTable'
import { privilegesContext } from 'src/context/privileges'

interface IAlterMedicinePopup {
    recordID: number | null
    modalID: any
    hospitalId: number | null
    remainingAmount: number
    mothlyAmount: number
    decisionId?: number | null
    patientData: PatientData | null
}


const MedicinesPopup: FC<IAlterMedicinePopup> = ({ modalID, recordID, hospitalId, remainingAmount, mothlyAmount, decisionId, patientData }) => {

    const { t } = useTranslation()
    const { state } = useLocation()
    const { hospitalCofigurations } = useContext(privilegesContext)

    const { mutate: submit, isPending: isLoadingSubmit } = useMutation()
    const { mutate: change, isPending: isLoadingChange } = useMutation()

    const [totalCost, setTotalCost] = useState(0)
    const [coveredMonth, setCoveredMonth] = useState(ALLOWED_MONTHS_TO_COVER[0].value)
    const [medicineList, setMedicineList] = useState([])
    const [requestIDToViewBatches, setRequestIDToViewBatches] = useState<number | null>(null)
    const [requestIdForPrint, setRequestIdForPrint] = useState<number | null>(null)

    const printRequestIsNeedCoolingMedicines = useRef<HTMLElement | null>(null);
    const printRequestMedicines = useRef<HTMLElement | null>(null);
    const batchDispensePopupRef = useRef<HTMLDivElement>(null);


    const { handlePrint } = usePrintImages({ title: "request-print" })

    const isAllowToDispenseMedicinesForMoreThanOneMonth = useMemo(() =>
        getHospitalConfiguralion(hospitalCofigurations, 'AllowToDispenseMedicinesForMoreThanOneMonthInOneRequest'), [hospitalCofigurations])

    const summaryRequestMedicinesModal = useRef<any>("")

    //@ts-ignore
    useEffect(() => {
        if (!medicineList?.length) return
        //@ts-ignore
        let tempActiveTotalCost = medicineList.filter(item => item.isSelected && !item.fromDonationsStock).map(item => Number(item?.cost || 0)).reduce((nxt, cur) => nxt + cur, 0)
        //@ts-ignore
        let tempAltTotalCost = medicineList.filter(item => !item.isSelected && item.altMedicine && !item?.isDefault && !item.fromDonationsStock).map(item => Number(item?.altPrice || 0)).reduce((nxt, cur) => nxt + cur, 0)
        setTotalCost((tempActiveTotalCost + tempAltTotalCost) * coveredMonth)
    }, [medicineList, coveredMonth])


    const handleCreateRequest = () => {
        if (!coveredMonth && isAllowToDispenseMedicinesForMoreThanOneMonth?.isAllowed) return
        //@ts-ignore
        let CheckedList = medicineList.filter(item => !!item?.isSelected).map(item => item.reqMedId)

        //@ts-ignore
        let AltList = medicineList.filter(item => !item?.isSelected && item?.altMedicine?.value && !item?.isDefault)

        if (!CheckedList?.length && !AltList?.length) return errorToaster("please-select-one-item")
        if (!state?.pharmName?.value) return errorToaster("select-hospital")
        //@ts-ignore

        let RequestBody = {
            pharmacyId: state?.pharmName?.value,
            requestId: recordID,
            requestMedicines: getRequestList(),
            alternativeMedicines: getAlternativeMedicines(),
            ...(isAllowToDispenseMedicinesForMoreThanOneMonth?.isAllowed && { coveredMonths: coveredMonth })
        }

        let requestSetup = {
            apiMethod: SubmitManualRequest,
            id: null,
            body: RequestBody,
            actionType: "post",
            customActionTitle: 'submit-manual-receive-order-confirmation',
            onSuccess: () => { handleResetSubmitRequest(); },
        }
        //@ts-ignore
        submit(requestSetup)
    }

    const selectedMedicinesSummaryList = useMemo(() => {
        return medicineList.filter((item: any) => item.isSelected || (item.altMedicine?.value && !item.isSelected && !item.isDefault))
    }, [medicineList])


    const getRequestList = () => {

        let requestBodyMedicine = medicineList.map((item: any) => (
            {
                "requestMedicineId": item?.reqMedId,
                "isAvailable": !item?.isModel56 ? item?.isSelected : false,
                "fromDonationsStock": item?.fromDonationsStock || false,
                "isModel56": !item?.isSelected ? item?.isModel56 : false,
            }
        ))
        return requestBodyMedicine
    }

    const getAlternativeMedicines = () => {
        //@ts-ignore
        let CheckedList = medicineList.filter(item => !!item?.isSelected).map(item => item.reqMedId)

        //@ts-ignore
        let AltList = medicineList.filter(item => !item?.isSelected && item?.altMedicine?.value && !item?.isDefault)


        if (!CheckedList?.length && !AltList?.length) {
            errorToaster("please-select-one-item")
            return []
        }


        let mappedListForRequest = AltList.map((item: any) => {
            return ({
                altToRequestMedicineId: item?.reqMedId,
                isEssential: item?.isEssential || false,
                fromDonationsStock: item.fromDonationsStock || false,
                AltMedicine: {
                    medicineId: item?.altMedicine?.value,
                    dosePerDay: item?.doseAmount,
                    doseCountPerTime: item?.doseRepeat,
                    doseTypeId: item?.userDoseRepeate?.value,
                    duration: item?.duration,
                    durationType: item?.durationType?.value,
                    directAmount: item?.altMedicine?.allowDirectAmount ? item?.directAmount : null,
                    allowsAllDurations: item?.allowAllDuration || false,
                    decisionId: !!item?.allowAllDuration ? decisionId : null,
                    isCustomAmount: item?.isCustomAmount || false
                }
            })
        }
        ) || []

        return mappedListForRequest
    }
    const changeRequest = () => {

        //@ts-ignore
        let tempList = medicineList.filter(item => !!item?.isSelected)?.map(item => item.reqMedId)

        //@ts-ignore
        let AltList = medicineList?.filter(item => !item?.isSelected)

        if (!tempList?.length && !AltList?.length) return errorToaster("please-select-one-item")

        //@ts-ignore
        let CheckedList = medicineList.filter(item => item?.isSelected).map(item => item?.reqMedId)

        let RequestBody = {
            requestId: recordID,
            needToChangeMedicinesIds: CheckedList
        }

        let requestSetup = {
            apiMethod: SubmitChangeRequestMedicinesRequest,
            id: null,
            body: RequestBody,
            actionType: "post",
            name: 'changeRequest',
            onSuccess: handleResetMedicinesPopup,
        }
        //@ts-ignore
        change(requestSetup)
    }

    useEffect(() => {
        if (!requestIDToViewBatches) return
        batchDispensePopupRef?.current?.click();
    }, [requestIDToViewBatches])

    const handleResetSubmitRequest = () => {
        summaryRequestMedicinesModal.current.click()
        setRequestIDToViewBatches(recordID)
        setMedicineList([])
        setCoveredMonth(ALLOWED_MONTHS_TO_COVER[0].value)
    }

    const handleResetMedicinesPopup = () => {
        modalID.current.click()
        setMedicineList([])
        setCoveredMonth(ALLOWED_MONTHS_TO_COVER[0].value)
    }

    return (
        <>
            <BatchDispensePopup
                modalId={batchDispensePopupRef}
                recordId={requestIDToViewBatches}
                handlePrint={() => setRequestIdForPrint(requestIDToViewBatches)}
            />

            <PrintRequest
                printRefs={[printRequestMedicines, printRequestIsNeedCoolingMedicines]}
                selectedPrintRequestId={requestIdForPrint}
                handlePrint={handlePrint}
                setSelectedPrintRequestId={setRequestIdForPrint}
            />

            <SummaryRequestMedicines
                selectedMedicinesSummaryList={selectedMedicinesSummaryList}
                patientData={patientData}
                modalID={summaryRequestMedicinesModal}
                handleCreateRequest={handleCreateRequest}
                medicinesHeaderPrices={{ medicinePrice: totalCost, remainingAmount: remainingAmount, allowedMonthlyAmount: mothlyAmount }}
                isLoadingSubmit={isLoadingSubmit}
                prevModalID={modalID}
                coveredMonths={coveredMonth}
            />
            <PopupContainer
                Name={"manual-receive"}
                modalName="ChangeMedicine"
                modalID={modalID}
                resetAllFields={handleResetMedicinesPopup}
                AdditionalTitle={` ${t('forOrderNumber', { orderNumber: recordID })}`}>

                <div className='row'>
                    <CostTag amount={totalCost} title={"total-cost"} />
                    <CostTag amount={remainingAmount} title={"remaining-amount"} />
                    <CostTag amount={mothlyAmount} title={"monthly-amount"} />
                </div>
                <MedicinesSubmitionPopup
                    isManual
                    isCustomAmount
                    recordID={recordID}
                    hospitalId={hospitalId}
                    medicineList={medicineList}
                    setMedicineList={setMedicineList}
                    ApiMethod={GetMedicinesForManualReceive}
                    isParamsValid={!!recordID}
                    depArrItems={[recordID, state?.pharmId, hospitalId]}
                    params={[recordID, state?.pharmName?.value || null, hospitalId]}
                    defaultValue={[]}
                    hasEssentialCheckbox={true}
                    decisionId={decisionId}
                    setCoveredMonth={setCoveredMonth}
                    coveredMonth={coveredMonth}
                />
                <div className='row'>
                    <div className='col-sm-6 '>
                        <SubmitBtn label={t('proccessed-to-order-summary')} onSubmit={() => summaryRequestMedicinesModal.current.click()} loading={isLoadingSubmit} disabled={!selectedMedicinesSummaryList || selectedMedicinesSummaryList.length === 0} />
                    </div>
                    <div className=' col-sm-6'>
                        <SubmitBtn label={t('change-request')} onSubmit={changeRequest} loading={isLoadingChange} className={'change-request'} />
                    </div>
                </div>
            </PopupContainer>
        </>
    )
}

export default MedicinesPopup